<template>
  <div class="border rounded-lg w-full">
    <!-- Header Section -->
    <div class="flex justify-between items-start mb-4 py-4 px-6 border-b">
      <div>
        <div class="font-bold">{{ uppercase(frequencyTitle) }}</div>
        <div
          class="flex items-center text-gray-500 text-sm mt-1"
          v-if="showRecurrence"
        >
          <ion-icon
            name="calendar-outline"
            class="w-4 h-4 mr-2"
          ></ion-icon>
          <span>{{ recurrenceText }}</span>
        </div>
      </div>
      <div
        class="flex sm:items-center sm:gap-4"
        v-if="showTotal"
      >
        <div class="hidden sm:flex sm:flex-col sm:justify-center">
          <div
            class="text-gray-500 text-sm font-bold uppercase"
            :class="{ 'pt-1': section.tax_amount === 0 }"
          >
            total
          </div>
          <div
            class="text-gray-400 text-xs"
            v-if="section.tax_amount !== 0"
          >
            (inc tax)
          </div>
        </div>
        <!-- Mobile Layout -->
        <div class="flex flex-col items-center sm:hidden">
          <div class="text-2xl font-bold">{{ formatCurrency(sectionTotal) }}</div>
          <div
            class="text-gray-400 text-xs"
            v-if="section.tax_amount !== 0"
          >
            (inc tax)
          </div>
        </div>
        <!-- Desktop Amount -->
        <div class="hidden sm:flex sm:items-center text-2xl font-bold">{{ formatCurrency(sectionTotal) }}</div>
      </div>
    </div>
    <!-- Services Table -->
    <div class="mt-6 pb-2">
      <div
        class="flex justify-between mb-2 font-bold px-6"
        v-if="hasServiceItems"
      >
        <div class="text-xs text-gray-500 uppercase">service</div>
        <div class="text-xs text-gray-500 uppercase">amount</div>
      </div>
      <!-- Service Items -->
      <div
        v-if="hasServiceItems"
        v-for="service in serviceItems"
        :key="service.id"
        class="pb-3 px-6"
      >
        <!-- Service Header -->
        <div
          @click="toggleService(service)"
          class="flex justify-between items-center py-2.5 rounded-lg px-2"
          :class="{ 'hover:bg-gray-50 cursor-pointer mb-1': service.description }"
        >
          <div class="flex items-center">
            <span class="text-gray-700 text-sm">{{ service.service }}</span>
            <span
              class="text-gray-500 text-sm ml-1"
              v-if="isDeposit"
              >({{ section.items[0].deposit_data.deposit.name }})</span
            >
            <span
              class="text-gray-500 text-sm ml-1"
              v-if="isBalance"
              >({{ section.items[0].deposit_data.balance.name }})</span
            >
            <ion-icon
              :name="expandedServices.includes(service.id) ? 'caret-up-outline' : 'caret-down-outline'"
              class="w-3 h-3 ml-2 transition-transform duration-200"
              v-if="service.description"
            ></ion-icon>
          </div>
          <div class="text-sm">
            {{ serviceItemAmount(service) }}
          </div>
        </div>

        <!-- Expandable Content -->
        <Transition
          enter-active-class="transition-all duration-200 ease-out"
          leave-active-class="transition-all duration-200 ease-in"
          enter-from-class="transform opacity-0 -translate-y-2"
          enter-to-class="transform opacity-100 translate-y-0"
          leave-from-class="transform opacity-100 translate-y-0"
          leave-to-class="transform opacity-0 -translate-y-2"
        >
          <div
            v-if="expandedServices.includes(service.id) && service.description"
            class="ml-5 px-4 py-3 border-gray-200 bg-gray-50 rounded-lg"
          >
            <div
              class="service-description text-sm text-gray-600 rounded-lg prose break-words"
              v-html="service.description"
            ></div>
          </div>
        </Transition>
      </div>
      <hr
        v-if="hasAddOnServiceItems && hasServiceItems"
        class="border-t border-dashed"
      />
      <div
        class="flex justify-between mb-2 font-bold px-6 mt-6"
        v-if="hasAddOnServiceItems"
      >
        <div class="text-xs text-gray-500 uppercase">add-ons</div>
        <div class="text-xs text-gray-500 uppercase">amount</div>
      </div>
      <!-- ADDONS -->
      <div
        v-if="hasAddOnServiceItems"
        v-for="service in addOnServiceItems"
        :key="service.id"
        class="mb-2 px-6"
      >
        <!-- Service Header -->
        <div
          @click.prevent="toggleService(service)"
          class="flex justify-between items-center py-2 rounded-lg px-2"
          :class="{ 'hover:bg-gray-50 cursor-pointer mb-1': service.description }"
        >
          <div class="flex items-center">
            <span class="text-gray-700 text-sm"
              >{{ service.service }}
              <span
                class="text-gray-500 text-sm ml-1"
                v-if="isDeposit"
                >({{ section.items[0].deposit_data.deposit.name }})</span
              >
              <span
                class="text-gray-500 text-sm ml-1"
                v-if="isBalance"
                >({{ section.items[0].deposit_data.balance.name }})</span
              >
            </span>
            <ion-icon
              :name="expandedServices.includes(service.id) ? 'caret-up-outline' : 'caret-down-outline'"
              class="w-3 h-3 ml-2 transition-transform duration-200"
              v-if="service.description"
            ></ion-icon>
          </div>
          <div class="flex items-center space-x-6">
            <portal-button
              :color="addServiceItemButtonColor(service)"
              @click.stop="updateSelectedAddOnServiceItems(service)"
              class="!px-5 !py-1"
              :disabled="!user?.can_choose_addons"
              v-tooltip="user?.can_choose_addons ? '' : 'Add-on selections have been finalized by another signatory'"
            >
              <ion-icon
                v-if="selectedAddOnServiceItems.includes(service.id)"
                name="checkmark-outline"
                class="text-white mr-2"
              ></ion-icon>

              {{ addOnServiceAddButtonText(service) }}
            </portal-button>
            <div class="text-sm text-right w-[100px]">
              {{ serviceTotal(service) }}{{ isHourlyFrequency ? "/hr" : "" }}
            </div>
          </div>
        </div>
        <!-- Expandable Content -->
        <Transition
          enter-active-class="transition-all duration-200 ease-out"
          leave-active-class="transition-all duration-200 ease-in"
          enter-from-class="transform opacity-0 -translate-y-2"
          enter-to-class="transform opacity-100 translate-y-0"
          leave-from-class="transform opacity-100 translate-y-0"
          leave-to-class="transform opacity-0 -translate-y-2"
        >
          <div
            v-if="expandedServices.includes(service.id) && service.description"
            class="ml-5 px-4 py-3 border-gray-200 bg-gray-50 rounded-lg"
          >
            <div
              class="text-sm text-gray-600 rounded-lg prose break-words"
              v-html="service.description"
            ></div>
          </div>
        </Transition>
      </div>
    </div>
    <div
      class="px-6 pb-4 flex flex-col items-end space-y-2"
      v-if="showTaxSection"
    >
      <div
        class="flex justify-end w-full"
        v-for="taxCode in tax_breakdown"
        :key="taxCode.tax_code_id"
      >
        <div class="text-xs text-gray-500 uppercase font-bold">{{ taxCodeInfo(taxCode) }}</div>
        <div class="w-24 text-right text-sm uppercase">{{ formatCurrency(taxCode.tax_amount) }}</div>
      </div>
    </div>
    <div
      class="px-6 pb-4 flex justify-end items-center"
      v-if="section.discount_amount"
    >
      <div class="text-xs text-gray-500 uppercase font-bold">{{ discountText }}</div>
      <div class="w-24 text-sm uppercase text-right">({{ formatCurrency(section.discount_amount) }})</div>
    </div>
  </div>
</template>

<script>
import currencyUtils from "../../../js/helpers/currencyUtils"
import PortalButton from "../../../ClientPortal/UI/PortalButtons/PortalButton.vue"
import Proposal from "../../repository/Proposal"

export default {
  components: { PortalButton },
  props: {
    section: {
      type: Object,
    },
    selectedAddOns: {
      type: Array,
      default: () => [],
    },
    isDeposit: {
      type: Boolean,
      required: false,
      default: false,
    },
    isBalance: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["set-selected-add-ons"],
  data() {
    return {
      user: window.FinancialCents.user,
      sectionTotal: this.section?.total,
      proposal: window.FinancialCents?.proposal,
      totalAmount: 330,
      expandedServices: [], // Tracks which services are expanded
      services: [],
      selectedAddOnServiceItems: [],
      tax_breakdown: this.section?.tax_breakdown || [],
    }
  },
  async mounted() {
    this.selectedAddOnServiceItems = this.selectedAddOns

    if (window.innerWidth >= 1024) {
      this.section.items.forEach((item) => {
        if (!this.isBalance) {
          this.expandedServices.push(item.id)
        }
      })
    }

    // Initialize tax breakdown from section data
    this.tax_breakdown = this.section?.tax_breakdown || []

    // Set total amount for non-optional deposit or balance items
    if (this.isDepositOrBalanceItem && !this.section.items[0].is_optional) {
      this.updateDepositOrBalanceTotal(this.section.items[0])
      return
    }

    // Set total amount for optional deposit or balance items
    if (this.isDepositOrBalanceItem) {
      this.updateDepositOrBalanceTotal(this.section.items[0])
      return
    }

    // Calculate total for regular items
    await this.calculateTotal()
  },

  watch: {
    selectedAddOns: {
      handler(newSelectedAddOns) {
        // Update selected add-ons and recalculate totals
        this.selectedAddOnServiceItems = newSelectedAddOns

        // Update total for deposit or balance items
        if (this.isDepositOrBalanceItem) {
          this.updateDepositOrBalanceTotal(this.section.items[0])
        }
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    frequencyTitle() {
      return this.isDepositOrBalanceItem ? "One-time" : this.section.frequency
    },

    isDepositOrBalanceItem() {
      return this.isDeposit || this.isBalance
    },

    isHourlyFrequency() {
      return this.section?.frequency.toLowerCase() === "hourly"
    },

    showRecurrence() {
      return !this.isHourlyFrequency
    },

    showTotal() {
      return !this.isHourlyFrequency
    },

    recurrenceText() {
      if (this.isDepositOrBalanceItem) {
        const { deposit_data } = this.section.items[0]
        return this.isDeposit
          ? deposit_data.deposit.bills_on_human_readable
          : deposit_data.balance.bills_on_human_readable
      }
      return this.section.bills_on_human_readable
    },

    discountText() {
      return this.section.discount_percentage
        ? `${this.section.discount_name} (${this.section.discount_percentage}%)`
        : this.section.discount_name
    },

    serviceItems() {
      return this.section.items.filter((item) => !item.is_optional)
    },

    addOnServiceItems() {
      return this.section.items.filter((item) => item.is_optional)
    },

    hasServiceItems() {
      return this.section.items.filter((item) => !item.is_optional).length > 0
    },

    hasAddOnServiceItems() {
      return this.section.items.filter((item) => item.is_optional).length > 0
    },

    showTaxSection() {
      return (
        this.section?.items.some((item) => item.is_taxable === true) &&
        this.tax_breakdown &&
        this.tax_breakdown.length > 0
      )
    },
  },

  methods: {
    updateDepositOrBalanceTotal(service) {
      // If service is not selected as an add-on, total is 0
      if (service.is_optional && !this.selectedAddOnServiceItems.includes(service.id)) {
        this.sectionTotal = 0
        return
      }

      // Set total based on whether it's a deposit or balance
      this.sectionTotal = this.isDeposit ? service.deposit_data.deposit.amount : service.deposit_data.balance.amount
    },

    taxCodeInfo(taxCode) {
      if (taxCode) {
        return `${taxCode.name} @ ${taxCode.tax_rate}% on ${this.formatCurrency(taxCode.taxable_amount)}`
      } else {
        return ""
      }
    },

    serviceItemAmount(service) {
      if (this.isDepositOrBalanceItem) {
        return `${this.formatCurrency(this.sectionTotal)}`
      } else {
        return `${this.formatCurrency(service.amount)}${this.isHourlyFrequency ? "/hr" : ""}`
      }
    },
    serviceTotal(service) {
      return this.isDepositOrBalanceItem
        ? this.isDeposit
          ? this.formatCurrency(service.deposit.amount)
          : this.formatCurrency(service.balance.amount)
        : `${this.formatCurrency(service.amount)}`
    },
    addOnServiceAddButtonText(service) {
      return this.selectedAddOnServiceItems.includes(service.id) ? "Added" : "Add"
    },

    addServiceItemButtonColor(service) {
      return this.selectedAddOnServiceItems.includes(service.id) || !this.user?.can_choose_addons
        ? "gray-bg"
        : "primary-bordered"
    },

    async updateSelectedAddOnServiceItems(service) {
      this.selectedAddOnServiceItems.includes(service.id)
        ? (this.selectedAddOnServiceItems = this.selectedAddOnServiceItems.filter((id) => id !== service.id))
        : this.selectedAddOnServiceItems.push(service.id)

      // Update total based on item type
      if (this.isDepositOrBalanceItem) {
        this.updateDepositOrBalanceTotal(service)
      } else {
        await this.calculateTotal()
      }

      this.$emit("set-selected-add-ons", this.selectedAddOnServiceItems)
    },

    async calculateTotal() {
      try {
        let { data } = await Proposal.calculate(this.proposal?.id, {
          section_id: this.section?.id,
          optional_ids: this.selectedAddOnServiceItems,
        })
        this.sectionTotal = data.data.total
        this.tax_breakdown = data.data.tax_breakdown || []
      } catch (e) {
        console.error(e)
      }
    },

    toggleService(service) {
      if (!service.description) {
        return
      }
      const index = this.expandedServices.indexOf(service.id)
      if (index === -1) {
        this.expandedServices.push(service.id)
      } else {
        this.expandedServices.splice(index, 1)
      }
    },

    uppercase(string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },

    formatCurrency(value) {
      return currencyUtils.convertAndFormatCurrency(value, this.proposal?.currency)
    },
  },
}
</script>
<style scoped>
:deep(.service-description.prose p:empty) {
  display: block;
  min-height: 1em;
}
</style>
